import React from 'react';
import { Box, Typography, Skeleton, useTheme, Tooltip } from '@mui/material';
import { TrendingUp, TrendingDown, TrendingFlat } from '@mui/icons-material';
import { MixBarCardProps } from '../types/analytics.types';

export const MixBarCard: React.FC<MixBarCardProps> = ({
  title,
  subtitle,
  segments,
  variance,
  yoy,
  loading = false,
  format = 'percentage',
  showLabels = true,
  segmentMinWidth = 10,
  tallHeight = false,
  cardCount = 3
}) => {
  const theme = useTheme();

  const formatValue = (val: number): string => {
    if (val === undefined || val === null) {
      return 'N/A';
    }

    switch (format) {
      case 'currency':
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(val);
      case 'number':
        return val.toLocaleString();
      case 'percentage':
      default:
        return `${val.toFixed(1)}%`;
    }
  };

  const getVarianceIcon = (direction: 'up' | 'down' | 'neutral') => {
    const iconProps = { 
      fontSize: 'small' as const,
      sx: { fontSize: '0.875rem' }
    };

    switch (direction) {
      case 'up':
        return <TrendingUp {...iconProps} sx={{ ...iconProps.sx, color: theme.palette.success.main }} />;
      case 'down':
        return <TrendingDown {...iconProps} sx={{ ...iconProps.sx, color: theme.palette.error.main }} />;
      case 'neutral':
      default:
        return <TrendingFlat {...iconProps} sx={{ ...iconProps.sx, color: theme.palette.grey[500] }} />;
    }
  };

  const getVarianceColor = (direction: 'up' | 'down' | 'neutral') => {
    switch (direction) {
      case 'up':
        return theme.palette.success.main;
      case 'down':
        return theme.palette.error.main;
      case 'neutral':
      default:
        return theme.palette.text.secondary;
    }
  };

  const cardHeight = tallHeight ? '12rem' : '10.5rem';
  
  // Responsive font sizes based on card count
  const getFontSize = (baseMin: string, baseVw: string, baseMax: string) => {
    if (cardCount >= 7) {
      return `clamp(${baseMin}, calc(${baseVw} * 0.7), calc(${baseMax} * 0.75))`;
    } else if (cardCount >= 5) {
      return `clamp(${baseMin}, calc(${baseVw} * 0.85), calc(${baseMax} * 0.875))`;
    }
    return `clamp(${baseMin}, ${baseVw}, ${baseMax})`;
  };
  
  const titleFontSize = getFontSize('0.75rem', '1.75vw', '0.875rem');
  const subtitleFontSize = getFontSize('0.7rem', '1.5vw', '0.8rem');
  const percentageFontSize = getFontSize('1rem', '3vw', '1.375rem');

  console.log(`[MixBarCard] ${title}:`, {
    loading,
    segmentsCount: segments?.length || 0,
    segments,
    format
  });

  if (loading) {
    return (
      <Box
        sx={{
          height: cardHeight,
          border: '1px solid',
          borderColor: theme.palette.divider,
          borderRadius: '0.5rem',
          padding: '1rem',
          backgroundColor: theme.palette.background.paper,
          display: 'flex',
          flexDirection: 'column',
          gap: '0.5rem',
        }}
      >
        <Skeleton variant="text" width="60%" height={24} />
        <Skeleton variant="rectangular" width="100%" height={32} sx={{ borderRadius: '0.25rem' }} />
        <Skeleton variant="text" width="40%" />
        <Skeleton variant="text" width="30%" />
      </Box>
    );
  }

  // Handle empty or invalid segments
  if (!segments || segments.length === 0) {
    return (
      <Box
        sx={{
          height: cardHeight,
          border: '1px solid',
          borderColor: theme.palette.divider,
          borderRadius: '0.5rem',
          padding: '1rem',
          backgroundColor: theme.palette.background.paper,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '0.5rem',
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
          {title}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          No data available
        </Typography>
      </Box>
    );
  }

  // Calculate total for verification
  const total = segments.reduce((sum, seg) => sum + seg.value, 0);

  console.log(`[MixBarCard] ${title} - Segment Details:`, {
    segments,
    total,
    segmentPercentages: segments.map(s => ({
      category: s.category,
      percentage: s.percentage,
      color: s.color,
      flex: s.percentage
    }))
  });

  return (
    <Box
      sx={{
        height: cardHeight,
        border: '1px solid',
        borderColor: theme.palette.divider,
        borderRadius: '0.5rem',
        padding: '1rem',
        backgroundColor: theme.palette.background.paper,
        display: 'flex',
        flexDirection: 'column',
        gap: '0.375rem',
        transition: 'all 0.2s ease',
        '&:hover': {
          borderColor: theme.palette.primary.main,
          boxShadow: theme.shadows[2],
        },
      }}
    >
      {/* Header with title and variance/yoy */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: '0.5rem',
        }}
      >
        {/* Left: Title and Subtitle */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.125rem', flex: 1, minWidth: 0 }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              color: theme.palette.text.primary,
              fontSize: titleFontSize,
              lineHeight: 1.2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography
              variant="caption"
              sx={{
                color: '#18181b',
                fontSize: subtitleFontSize,
                lineHeight: 1.2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>

        {/* Right: Variance and YoY */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.125rem' }}>
          {variance && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
              {getVarianceIcon(variance.direction)}
              <Typography
                variant="caption"
                sx={{
                  color: getVarianceColor(variance.direction),
                  fontWeight: 600,
                  fontSize: subtitleFontSize,
                  whiteSpace: 'nowrap',
                }}
              >
                {variance.value >= 0 ? '+' : ''}{variance.value.toFixed(1)}%
              </Typography>
            </Box>
          )}
          {yoy && (
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: `calc(${subtitleFontSize} * 0.9)`,
                whiteSpace: 'nowrap',
              }}
            >
              YoY {yoy.value >= 0 ? '+' : ''}{yoy.value.toFixed(1)}%
            </Typography>
          )}
        </Box>
      </Box>

      {/* Stacked bar */}
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          height: '32px',
          borderRadius: '0.25rem',
          mt: '0.5rem',
        }}
      >
        {segments.map((segment, index) => {
          console.log(`[MixBarCard] Rendering segment ${index}:`, {
            category: segment.category,
            percentage: segment.percentage,
            color: segment.color,
            widthStyle: `${segment.percentage}%`
          });
          
          return (
            <Box
              key={index}
              sx={{
                width: `${segment.percentage}%`,
                height: '100%',
                backgroundColor: segment.color,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                position: 'relative',
                flexShrink: 0,
                border: `1px solid ${segment.color}`, // Debug: make sure it has some boundary
                '&:hover': {
                  filter: 'brightness(1.1)',
                  transform: 'scaleY(1.05)',
                },
              }}
              title={`${segment.category}: ${formatValue(segment.value)} (${segment.percentage.toFixed(1)}%)`}
            >
              {showLabels && segment.percentage >= segmentMinWidth && (
                <Typography
                  variant="caption"
                  sx={{
                    color: '#fff',
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                    position: 'relative',
                    zIndex: 1,
                  }}
                >
                  {segment.percentage.toFixed(0)}%
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>

      {/* Legend */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '0.5rem',
          mt: '0.5rem',
        }}
      >
        {segments.map((segment, index) => (
          <Box
            key={index}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.25rem',
            }}
          >
            <Box
              sx={{
                width: '0.75rem',
                height: '0.75rem',
                borderRadius: '0.125rem',
                backgroundColor: segment.color,
              }}
            />
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: subtitleFontSize,
                whiteSpace: 'nowrap',
              }}
            >
              {segment.category}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

